'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AnimatedGroup } from '@/components/ui/animated-group'
import { HeroHeader } from '@/components/header'
import Footer from '@/components/footer'
import { 
  Zap, 
  Users,
  Brain,
  MessageSquare,
  BarChart3,
  Shield,
  Smartphone,
  Globe,
  ArrowRight,
  Check,
  Play,
  Star,
  Mic,
  Target,
  Calendar,
  QrCode,
  GamepadIcon,
  TrendingUp,
  Lock,
  Headphones,
  Wifi
} from 'lucide-react'

const mainFeatures = [
  {
    icon: Brain,
    title: "AI-Powered Smart Matching",
    description: "Our intelligent algorithm analyzes interests, goals, and professional backgrounds to suggest the most relevant connections at your event.",
    benefits: [
      "Matches based on shared interests and complementary skills",
      "Real-time compatibility scoring",
      "Suggests optimal conversation starters",
      "Learns from successful interactions"
    ],
    bgColor: "bg-[#ffaf68]",
    rotateClass: "rotate-2",
    demo: "Watch how AI finds your perfect networking match in seconds"
  },
  {
    icon: Mic,
    title: "Voice-Activated Networking",
    description: "Simply speak to discover connections, join conversations, and access event information - no typing required.",
    benefits: [
      "Hands-free event navigation",
      "Voice commands for finding people",
      "Spoken conversation prompts",
      "Accessibility-first design"
    ],
    bgColor: "bg-[#70b5ff]",
    rotateClass: "-rotate-1",
    demo: "Try voice commands: 'Find someone interested in AI' or 'Start a conversation'"
  },
  {
    icon: GamepadIcon,
    title: "Interactive Networking Games",
    description: "Break the ice with engaging games like networking bingo, conversation challenges, and team-building activities.",
    benefits: [
      "Networking bingo with custom challenges",
      "Icebreaker conversation games",
      "Team formation activities",
      "Achievement systems and leaderboards"
    ],
    bgColor: "bg-[#79d45e]",
    rotateClass: "rotate-3",
    demo: "Experience how games make networking fun and natural"
  },
  {
    icon: BarChart3,
    title: "Real-Time Analytics",
    description: "Get instant insights into networking patterns, connection quality, and event engagement to optimize your networking strategy.",
    benefits: [
      "Live connection tracking",
      "Engagement quality metrics",
      "Networking effectiveness scores",
      "Personalized improvement suggestions"
    ],
    bgColor: "bg-[#f4889a]",
    rotateClass: "-rotate-2",
    demo: "See your networking impact visualized in real-time"
  }
]

const additionalFeatures = [
  {
    icon: QrCode,
    title: "Instant Event Join",
    description: "Join events instantly with QR codes - no complex registration process",
    bgColor: "bg-[#ba81ee]",
    rotateClass: "rotate-1"
  },
  {
    icon: MessageSquare,
    title: "Smart Conversation Starters",
    description: "AI-generated conversation prompts based on shared interests",
    bgColor: "bg-[#4928fd]",
    rotateClass: "-rotate-3"
  },
  {
    icon: Calendar,
    title: "Follow-Up Reminders",
    description: "Automated reminders to connect with your new contacts",
    bgColor: "bg-[#ffaf68]",
    rotateClass: "rotate-2"
  },
  {
    icon: Shield,
    title: "Privacy Controls",
    description: "Complete control over what information you share and with whom",
    bgColor: "bg-[#79d45e]",
    rotateClass: "-rotate-1"
  },
  {
    icon: TrendingUp,
    title: "Connection Quality Scoring",
    description: "Intelligent scoring system to help you focus on high-value connections",
    bgColor: "bg-[#70b5ff]",
    rotateClass: "rotate-3"
  },
  {
    icon: Globe,
    title: "Multi-Language Support",
    description: "Connect with international professionals in their preferred language",
    bgColor: "bg-[#f4889a]",
    rotateClass: "-rotate-2"
  }
]

const integrations = [
  {
    name: "LinkedIn",
    description: "Sync professional profiles and connect seamlessly",
    bgColor: "bg-[#0077b5]"
  },
  {
    name: "Zoom",
    description: "Integrate with virtual events and breakout rooms",
    bgColor: "bg-[#2d8cff]"
  },
  {
    name: "Salesforce",
    description: "Sync leads and track networking ROI",
    bgColor: "bg-[#00a1e0]"
  },
  {
    name: "Slack",
    description: "Continue conversations in your team workspace",
    bgColor: "bg-[#4a154b]"
  }
]

const transitionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

export default function FeaturesPage() {
  const [activeFeature, setActiveFeature] = useState(0)

  return (
    <div className="w-full" style={{ backgroundColor: '#fdf1d3' }}>
      <HeroHeader />
      <main className="relative overflow-hidden">
        {/* Scattered decorative elements */}
        <div className="absolute top-20 left-10 w-8 h-8 bg-[#4928fd] border-4 border-black transform rotate-45 shadow-xl"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-[#ba81ee] border-4 border-black rounded-full shadow-xl"></div>
        <div className="absolute bottom-32 left-1/4 w-5 h-12 bg-[#79d45e] border-4 border-black transform -rotate-12 shadow-xl"></div>

        {/* Floating sticky note */}
        <div className="absolute top-32 right-4 bg-[#f6e683] border-4 border-black p-3 transform rotate-12 shadow-xl max-w-32 hidden md:block">
          <p className="text-xs font-black text-black">"Every feature designed for real connections!"</p>
        </div>

        {/* Hero Section */}
        <section className="relative px-6 py-16 md:py-32">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-3 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <Badge variant="outline" className="border-white text-white bg-transparent">
                  <Zap className="mr-2 h-4 w-4" />
                  POWERFUL FEATURES
                </Badge>
              </div>
            </AnimatedGroup>

            <div className="bg-[#ffaf68] border-4 border-black p-8 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-black leading-tight">
                NETWORKING<br />MADE SIMPLE<br />& EFFECTIVE
              </h1>
            </div>

            <div className="bg-white border-4 border-black p-6 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 max-w-3xl">
              <p className="text-lg font-semibold text-black">
                Discover the intelligent features that transform awkward networking into meaningful connections. 
                Built by a developer who understands the pain of traditional networking events.
              </p>
            </div>
          </div>
        </section>

        {/* Main Features */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#d9f3fc' }}>
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-16">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#79d45e] border-4 border-black p-3 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                  <Badge variant="outline" className="border-black text-black bg-transparent">
                    <Star className="mr-2 h-4 w-4" />
                    CORE FEATURES
                  </Badge>
                </div>
              </AnimatedGroup>
              
              <div className="bg-[#70b5ff] border-4 border-black p-6 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-black">
                  THE FEATURES THAT MAKE<br />NETWORKING ACTUALLY WORK
                </h2>
              </div>
            </div>

            <div className="space-y-16">
              {mainFeatures.map((feature, index) => (
                <AnimatedGroup
                  key={feature.title}
                  variants={{
                    item: {
                      ...transitionVariants,
                      visible: {
                        ...transitionVariants.visible,
                        transition: {
                          ...transitionVariants.visible.transition,
                          delay: index * 0.1
                        }
                      }
                    }
                  }}>
                  <div className={`grid gap-8 lg:grid-cols-2 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                    <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                      <div className={`${feature.bgColor} border-4 border-black p-8 transform hover:rotate-0 transition-all duration-200 shadow-xl ${feature.rotateClass}`}>
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-16 h-16 bg-white border-4 border-black rounded-full flex items-center justify-center">
                            <feature.icon className="h-8 w-8 text-black" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-black text-black mb-2">{feature.title}</h3>
                            <p className="text-black font-semibold">{feature.description}</p>
                          </div>
                        </div>

                        <div className="space-y-3 mb-6">
                          {feature.benefits.map((benefit, benefitIndex) => (
                            <div key={benefitIndex} className="flex items-start gap-3">
                              <Check className="h-5 w-5 text-black mt-0.5 flex-shrink-0" />
                              <span className="text-black font-semibold text-sm">{benefit}</span>
                            </div>
                          ))}
                        </div>

                        <div className="bg-white border-4 border-black p-4 transform rotate-1 hover:rotate-0 transition-transform duration-200">
                          <div className="flex items-center gap-3">
                            <Play className="h-5 w-5 text-black" />
                            <span className="text-black font-black text-sm">{feature.demo}</span>
                          </div>
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-white border-2 border-black transform rotate-45"></div>
                        <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-black rounded-full"></div>
                      </div>
                    </div>

                    <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                      <div className="bg-white border-4 border-black p-8 transform -rotate-1 hover:rotate-0 transition-transform duration-200 shadow-xl h-64 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-24 h-24 bg-gray-200 border-4 border-black rounded-lg mx-auto mb-4 flex items-center justify-center">
                            <feature.icon className="h-12 w-12 text-black" />
                          </div>
                          <p className="font-black text-black">Interactive Demo</p>
                          <p className="font-semibold text-black/70 text-sm">Click to experience this feature</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </AnimatedGroup>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Features Grid */}
        <section className="relative px-6 py-16">
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#4928fd] border-4 border-black p-3 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                  <Badge variant="outline" className="border-white text-white bg-transparent">
                    <Target className="mr-2 h-4 w-4" />
                    MORE FEATURES
                  </Badge>
                </div>
              </AnimatedGroup>
              
              <div className="bg-[#f4889a] border-4 border-black p-6 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                <h2 className="text-3xl md:text-4xl font-black text-black">
                  EVEN MORE WAYS TO CONNECT
                </h2>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {additionalFeatures.map((feature, index) => (
                <AnimatedGroup
                  key={feature.title}
                  variants={{
                    item: {
                      ...transitionVariants,
                      visible: {
                        ...transitionVariants.visible,
                        transition: {
                          ...transitionVariants.visible.transition,
                          delay: index * 0.1
                        }
                      }
                    }
                  }}>
                  <div className={`${feature.bgColor} border-4 border-black p-6 transform hover:rotate-0 transition-all duration-200 shadow-xl ${feature.rotateClass} h-full text-center`}>
                    <div className="mx-auto mb-4 w-12 h-12 bg-white border-4 border-black rounded-full flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-black" />
                    </div>
                    <h3 className="text-lg font-black text-black mb-3">{feature.title}</h3>
                    <p className="text-black font-semibold text-sm">{feature.description}</p>
                    
                    {/* Decorative elements */}
                    <div className="absolute -top-2 -left-2 w-4 h-4 bg-white border-2 border-black transform rotate-45"></div>
                    <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-black rounded-full"></div>
                  </div>
                </AnimatedGroup>
              ))}
            </div>
          </div>
        </section>

        {/* Platform Features */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#ba81ee' }}>
          <div className="mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-white border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200">
                  <h2 className="text-3xl md:text-4xl font-black text-black">
                    WORKS EVERYWHERE YOU DO
                  </h2>
                </div>
              </AnimatedGroup>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#ffaf68] border-4 border-black p-6 transform rotate-2 hover:rotate-0 transition-all duration-200 shadow-xl text-center">
                  <Smartphone className="h-12 w-12 text-black mx-auto mb-4" />
                  <h3 className="font-black text-black mb-2">Mobile App</h3>
                  <p className="text-black font-semibold text-sm">iOS & Android native apps for on-the-go networking</p>
                </div>
              </AnimatedGroup>

              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#70b5ff] border-4 border-black p-6 transform -rotate-1 hover:rotate-0 transition-all duration-200 shadow-xl text-center">
                  <Globe className="h-12 w-12 text-black mx-auto mb-4" />
                  <h3 className="font-black text-black mb-2">Web Platform</h3>
                  <p className="text-black font-semibold text-sm">Full-featured web app accessible from any browser</p>
                </div>
              </AnimatedGroup>

              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#79d45e] border-4 border-black p-6 transform rotate-3 hover:rotate-0 transition-all duration-200 shadow-xl text-center">
                  <Wifi className="h-12 w-12 text-black mx-auto mb-4" />
                  <h3 className="font-black text-black mb-2">Offline Mode</h3>
                  <p className="text-black font-semibold text-sm">Continue networking even without internet connection</p>
                </div>
              </AnimatedGroup>

              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#f4889a] border-4 border-black p-6 transform -rotate-2 hover:rotate-0 transition-all duration-200 shadow-xl text-center">
                  <Headphones className="h-12 w-12 text-black mx-auto mb-4" />
                  <h3 className="font-black text-black mb-2">Voice Integration</h3>
                  <p className="text-black font-semibold text-sm">Hands-free operation with voice commands</p>
                </div>
              </AnimatedGroup>
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="relative px-6 py-16">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-[#79d45e] border-4 border-black p-6 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                  <h2 className="text-3xl md:text-4xl font-black text-black">
                    INTEGRATES WITH YOUR FAVORITE TOOLS
                  </h2>
                </div>
              </AnimatedGroup>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {integrations.map((integration, index) => (
                <AnimatedGroup
                  key={integration.name}
                  variants={{
                    item: {
                      ...transitionVariants,
                      visible: {
                        ...transitionVariants.visible,
                        transition: {
                          ...transitionVariants.visible.transition,
                          delay: index * 0.1
                        }
                      }
                    }
                  }}>
                  <div className={`${integration.bgColor} border-4 border-black p-4 text-center text-white transform hover:rotate-0 transition-all duration-200 shadow-xl rotate-1`}>
                    <h3 className="font-black mb-2">{integration.name}</h3>
                    <p className="text-xs font-semibold opacity-90">{integration.description}</p>
                  </div>
                </AnimatedGroup>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#d9f3fc' }}>
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <h2 className="text-3xl md:text-4xl font-black text-white">
                  Ready to Experience the Future?
                </h2>
              </div>
              <div className="bg-white border-4 border-black p-4 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 mb-8">
                <p className="text-lg font-semibold text-black">
                  See how these features work together to create networking experiences that actually deliver results.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="bg-[#79d45e] border-4 border-black transform rotate-2 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-[#79d45e] hover:bg-[#79d45e]/90 text-black border-0 font-black" asChild>
                    <Link href="/signup">
                      Try All Features Free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                <div className="bg-white border-4 border-black transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-white hover:bg-gray-50 text-black border-0 font-black" asChild>
                    <Link href="/contact">
                      Request Demo
                    </Link>
                  </Button>
                </div>
              </div>
            </AnimatedGroup>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
